import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, HandshakeIcon, TrendingUpIcon, CheckCircle2Icon, ArrowRightIcon, DownloadIcon, MapPinIcon, UsersIcon, DatabaseIcon, NetworkIcon, BuildingIcon, MailIcon, BarChart3Icon, PieChartIcon, SparklesIcon } from 'lucide-react';
const dashboardData = [{
  header: <>
        I found <strong className="text-white" data-id="element-3072">28 qualified leads</strong>. Here
        are the top results scored by revenue potential and intent:
      </>,
  table: [{
    name: 'Supercuts',
    domain: 'supercuts.com',
    size: '50–200',
    score: 94
  }, {
    name: 'Sephora',
    domain: 'sephora.com',
    size: '200+',
    score: 91
  }, {
    name: 'SmartStyle',
    domain: 'smartstyle.com',
    size: '50–200',
    score: 87
  }, {
    name: 'First Choice Hair',
    domain: 'firstchoicehaircutters.com',
    size: '10–50',
    score: 82
  }, {
    name: 'Chatters',
    domain: 'chatters.ca',
    size: '10–50',
    score: 79
  }],
  barChartTitle: 'LOCATION COUNT BY CHAIN',
  barChartData: [36, 12, 10, 8, 6, 5, 3],
  donutTitle: 'SCORE DIMENSION BREAKDOWN',
  donutData: [{
    label: 'High intent',
    value: '33%',
    color: 'bg-blue-500'
  }, {
    label: 'Strong ICP fit',
    value: '25%',
    color: 'bg-indigo-500'
  }, {
    label: 'Revenue potential',
    value: '20%',
    color: 'bg-blue-400'
  }, {
    label: 'Tech ready',
    value: '13%',
    color: 'bg-sky-400'
  }, {
    label: 'Other',
    value: '9%',
    color: 'bg-slate-600'
  }],
  donutGradient: 'conic-gradient(#3b82f6 0% 33%, #6366f1 33% 58%, #60a5fa 58% 78%, #38bdf8 78% 91%, #475569 91% 100%)'
}, {
  header: <>
        I found <strong className="text-white" data-id="element-3073">15 potential partners</strong>.
        Top matches scored by audience overlap and location:
      </>,
  table: [{
    name: 'Planet Fitness',
    domain: 'planetfitness.com',
    size: '200+',
    score: 96
  }, {
    name: 'Anytime Fitness',
    domain: 'anytimefitness.com',
    size: '200+',
    score: 92
  }, {
    name: "Gold's Gym",
    domain: 'goldsgym.com',
    size: '50–200',
    score: 88
  }, {
    name: 'Crunch',
    domain: 'crunch.com',
    size: '50–200',
    score: 85
  }, {
    name: 'LA Fitness',
    domain: 'lafitness.com',
    size: '200+',
    score: 81
  }],
  barChartTitle: 'MEMBER OVERLAP BY REGION',
  barChartData: [42, 28, 20, 15, 12, 8, 5],
  donutTitle: 'SYNERGY BREAKDOWN',
  donutData: [{
    label: 'Audience match',
    value: '40%',
    color: 'bg-blue-500'
  }, {
    label: 'Location proximity',
    value: '30%',
    color: 'bg-indigo-500'
  }, {
    label: 'Brand alignment',
    value: '15%',
    color: 'bg-blue-400'
  }, {
    label: 'Price point',
    value: '10%',
    color: 'bg-sky-400'
  }, {
    label: 'Other',
    value: '5%',
    color: 'bg-slate-600'
  }],
  donutGradient: 'conic-gradient(#3b82f6 0% 40%, #6366f1 40% 70%, #60a5fa 70% 85%, #38bdf8 85% 95%, #475569 95% 100%)'
}, {
  header: <>
        Analyzed <strong className="text-white" data-id="element-3074">42 competitors</strong>. Top
        threats scored by market share and growth rate:
      </>,
  table: [{
    name: 'Starbucks',
    domain: 'starbucks.com',
    size: '200+',
    score: 98
  }, {
    name: "Dunkin'",
    domain: 'dunkindonuts.com',
    size: '200+',
    score: 95
  }, {
    name: "Peet's",
    domain: 'peets.com',
    size: '50–200',
    score: 89
  }, {
    name: 'Dutch Bros',
    domain: 'dutchbros.com',
    size: '50–200',
    score: 86
  }, {
    name: 'Caribou',
    domain: 'cariboucoffee.com',
    size: '50–200',
    score: 83
  }],
  barChartTitle: 'MARKET SHARE BY REGION',
  barChartData: [50, 35, 25, 20, 15, 10, 5],
  donutTitle: 'GROWTH FACTORS',
  donutData: [{
    label: 'Foot traffic',
    value: '35%',
    color: 'bg-blue-500'
  }, {
    label: 'New locations',
    value: '25%',
    color: 'bg-indigo-500'
  }, {
    label: 'Menu expansion',
    value: '20%',
    color: 'bg-blue-400'
  }, {
    label: 'Digital sales',
    value: '15%',
    color: 'bg-sky-400'
  }, {
    label: 'Other',
    value: '5%',
    color: 'bg-slate-600'
  }],
  donutGradient: 'conic-gradient(#3b82f6 0% 35%, #6366f1 35% 60%, #60a5fa 60% 80%, #38bdf8 80% 95%, #475569 95% 100%)'
}];
const useCases = [{
  id: 'lead-gen',
  title: 'Lead generation',
  icon: TargetIcon,
  description: 'Build targeted prospect lists for your sales team',
  features: ['Find qualified leads in any location', 'Filter by business type and size', 'Export ready-to-use contact lists', 'Download as CSV or Google Sheets'],
  visual: 'crm'
}, {
  id: 'partnerships',
  title: 'Partnership development',
  icon: HandshakeIcon,
  description: 'Identify and connect with potential partners',
  features: ['Discover complementary businesses', 'Research local market opportunities', 'Find franchise expansion locations', 'Build supplier/vendor networks'],
  visual: 'network'
}, {
  id: 'research',
  title: 'Market research',
  icon: TrendingUpIcon,
  description: 'Analyze local business landscapes and trends',
  features: ['Competitive intelligence gathering', 'Market penetration analysis', 'Location-based opportunity mapping', 'Industry trend identification'],
  visual: 'chart'
}];
function CrmVisual() {
  const rows = [{
    name: "Bella's Bakery",
    type: 'Bakery',
    status: 'Verified'
  }, {
    name: 'Peak Fitness Co.',
    type: 'Gym',
    status: 'Verified'
  }, {
    name: 'Urban Dental Care',
    type: 'Dentist',
    status: 'Pending'
  }];
  return <motion.div key="crm" initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.4
  }} className="w-full max-w-sm mx-auto" data-id="element-3075">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden" data-id="element-3076">
        <div className="bg-slate-800 px-4 py-2.5 border-b border-slate-700 flex items-center justify-between" data-id="element-3077">
          <div className="flex gap-1.5" data-id="element-3078">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" data-id="element-3079" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" data-id="element-3080" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" data-id="element-3081" />
          </div>
          <motion.div animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-[10px] font-medium text-blue-400 flex items-center gap-1" data-id="element-3082">
            <DownloadIcon className="w-3 h-3" data-id="element-3083" /> Export to CSV
          </motion.div>
        </div>
        {/* Table header */}
        <div className="px-4 py-2 border-b border-slate-800 flex items-center text-[9px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-3084">
          <span className="flex-1" data-id="element-3085">Business</span>
          <span className="w-16 text-center" data-id="element-3086">Type</span>
          <span className="w-16 text-right" data-id="element-3087">Status</span>
        </div>
        <div className="p-2 space-y-1" data-id="element-3088">
          {rows.map((row, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4,
          delay: 0.2 + i * 0.15
        }} className="flex items-center px-2 py-2 rounded-lg hover:bg-slate-800/60 transition-colors" data-id="element-3089">
              <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-3090">
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.5
            }} className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0" data-id="element-3091">
                  <BuildingIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-3092" />
                </motion.div>
                <div className="min-w-0" data-id="element-3093">
                  <div className="text-xs font-semibold text-slate-200 truncate" data-id="element-3094">
                    {row.name}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-3095">
                    <MailIcon className="w-2.5 h-2.5" data-id="element-3096" /> Email available
                  </div>
                </div>
              </div>
              <span className="w-16 text-center text-[10px] text-slate-400 font-medium" data-id="element-3097">
                {row.type}
              </span>
              <div className="w-16 flex justify-end" data-id="element-3098">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${row.status === 'Verified' ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20'}`} data-id="element-3099">
                  {row.status}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}
function NetworkVisual() {
  // All positions as percentages (x% of width, y% of height)
  const nodes = [{
    x: 12.5,
    y: 20,
    icon: DatabaseIcon,
    size: 'sm' as const,
    delay: 0
  }, {
    x: 50,
    y: 47.5,
    icon: NetworkIcon,
    size: 'lg' as const,
    delay: 0.2
  }, {
    x: 87.5,
    y: 20,
    icon: MapPinIcon,
    size: 'sm' as const,
    delay: 0.4
  }, {
    x: 30,
    y: 77.5,
    icon: BuildingIcon,
    size: 'sm' as const,
    delay: 0.3
  }, {
    x: 70,
    y: 77.5,
    icon: UsersIcon,
    size: 'sm' as const,
    delay: 0.5
  }];
  const lines = [{
    x1: 12.5,
    y1: 20,
    x2: 50,
    y2: 47.5
  }, {
    x1: 50,
    y1: 47.5,
    x2: 87.5,
    y2: 20
  }, {
    x1: 30,
    y1: 77.5,
    x2: 50,
    y2: 47.5
  }, {
    x1: 50,
    y1: 47.5,
    x2: 70,
    y2: 77.5
  }];
  return <motion.div key="network" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className="relative w-full h-full" data-id="element-3100">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-3101">
        {lines.map((line, i) => <motion.line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="2 1.5" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.4
      }} transition={{
        duration: 0.8,
        delay: 0.3 + i * 0.15
      }} data-id="element-3102" />)}
      </svg>
      {nodes.map((node, i) => {
      const Icon = node.icon;
      const isCenter = node.size === 'lg';
      return <motion.div key={i} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        delay: node.delay,
        type: 'spring',
        stiffness: 200
      }} className="absolute" style={{
        left: `${node.x}%`,
        top: `${node.y}%`,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-3103">
            {/* Pulse ring */}
            <motion.div animate={{
          opacity: [0, 0.3, 0],
          scale: [0.8, 1.6, 0.8]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: node.delay + 1
        }} className={`absolute inset-0 rounded-full border border-blue-500 ${isCenter ? 'scale-150' : ''}`} data-id="element-3104" />
            <motion.div animate={isCenter ? {} : {
          y: [0, -4, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: node.delay,
          ease: 'easeInOut'
        }} className={`rounded-full flex items-center justify-center ${isCenter ? 'w-14 h-14 bg-blue-600 shadow-xl shadow-blue-600/30 border-2 border-blue-500' : 'w-10 h-10 bg-slate-800 border-2 border-slate-700 shadow-lg'}`} data-id="element-3105">
              <Icon className={`${isCenter ? 'w-6 h-6 text-white' : 'w-4 h-4 text-slate-400'}`} data-id="element-3106" />
            </motion.div>
          </motion.div>;
    })}
    </motion.div>;
}
function ChartVisual() {
  const bars = [{
    height: 35,
    label: 'Q1'
  }, {
    height: 55,
    label: 'Q2'
  }, {
    height: 40,
    label: 'Q3'
  }, {
    height: 80,
    label: 'Q4'
  }, {
    height: 60,
    label: 'Q5'
  }, {
    height: 72,
    label: 'Q6'
  }];
  return <motion.div key="chart" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className="w-full h-full flex flex-col px-8 pt-6 pb-4" data-id="element-3107">
      {/* Y-axis grid lines */}
      <div className="flex-1 relative" data-id="element-3108">
        {[0, 1, 2, 3].map(i => <div key={i} className="absolute w-full border-t border-slate-800/50" style={{
        top: `${i * 33}%`
      }} data-id="element-3109" />)}
        <div className="absolute inset-0 flex items-end justify-center gap-4" data-id="element-3110">
          {bars.map((bar, i) => <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative" data-id="element-3111">
              <motion.div initial={{
            height: 0
          }} animate={{
            height: `${bar.height}%`
          }} transition={{
            duration: 0.6,
            delay: 0.2 + i * 0.1,
            ease: [0.34, 1.56, 0.64, 1]
          }} className={`w-full max-w-[40px] rounded-t-md relative ${i === 3 ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-3112">
                {i === 3 && <motion.div initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1
            }} className="absolute -top-7 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap shadow-lg shadow-blue-600/30" data-id="element-3113">
                    +24%
                  </motion.div>}
                {i === 3 && <motion.div animate={{
              opacity: [0.3, 0.6, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className="absolute inset-0 rounded-t-md bg-blue-400/20" data-id="element-3114" />}
              </motion.div>
            </div>)}
        </div>
      </div>
      {/* X-axis labels */}
      <div className="flex justify-center gap-4 mt-2 pt-2 border-t border-slate-800/50" data-id="element-3115">
        {bars.map((bar, i) => <motion.span key={i} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5 + i * 0.05
      }} className={`flex-1 text-center text-[9px] font-medium ${i === 3 ? 'text-blue-400' : 'text-slate-600'}`} data-id="element-3116">
            {bar.label}
          </motion.span>)}
      </div>
    </motion.div>;
}
export function PowerfulUseCases() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeCase = useCases[activeIndex];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-3117">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-3118">
        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-3119">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl font-bold mb-4 text-slate-900" data-id="element-3120">
            Powerful <span className="text-blue-600" data-id="element-3121">use cases</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-lg text-slate-500" data-id="element-3122">
            Transform local business data into actionable insights and revenue
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 relative z-10" data-id="element-3123">
          {/* Left Column: Interactive Tabs */}
          <div className="lg:col-span-5 space-y-4" data-id="element-3124">
            {useCases.map((useCase, index) => {
            const isActive = index === activeIndex;
            const Icon = useCase.icon;
            return <motion.button key={useCase.id} onClick={() => setActiveIndex(index)} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 group ${isActive ? 'bg-blue-50/50 border-blue-500 shadow-lg shadow-blue-500/10' : 'bg-white border-slate-100 hover:border-blue-200 hover:bg-slate-50'}`} data-id="element-3125">
                  <div className="flex items-start gap-4" data-id="element-3126">
                    <div className={`p-3 rounded-xl transition-colors duration-300 shrink-0 ${isActive ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'}`} data-id="element-3127">
                      <Icon className="w-6 h-6" data-id="element-3128" />
                    </div>
                    <div data-id="element-3129">
                      <h3 className={`text-xl font-bold mb-1 transition-colors duration-300 ${isActive ? 'text-blue-900' : 'text-slate-900 group-hover:text-blue-700'}`} data-id="element-3130">
                        {useCase.title}
                      </h3>
                      <p className={`text-sm transition-colors duration-300 ${isActive ? 'text-blue-700/80' : 'text-slate-500'}`} data-id="element-3131">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                </motion.button>;
          })}
          </div>

          {/* Right Column: Dynamic Content Area */}
          <div className="lg:col-span-7" data-id="element-3132">
            <div className="bg-slate-900 rounded-3xl h-full relative overflow-hidden shadow-2xl shadow-slate-900/20 border border-slate-800" data-id="element-3133">
              {/* Dynamic Visual Header */}
              <div className="h-52 bg-slate-800/30 border-b border-slate-800 relative overflow-hidden flex items-center justify-center" data-id="element-3134">
                <AnimatePresence mode="wait" data-id="element-3135">
                  {activeCase.visual === 'crm' && <CrmVisual data-id="element-3136" />}
                  {activeCase.visual === 'network' && <NetworkVisual data-id="element-3137" />}
                  {activeCase.visual === 'chart' && <ChartVisual data-id="element-3138" />}
                </AnimatePresence>
              </div>

              {/* Bottom Content Area */}
              <div className="p-6 flex-1 flex flex-col" data-id="element-3139">
                <AnimatePresence mode="wait" data-id="element-3140">
                  <motion.div key={activeIndex} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} data-id="element-3141">
                    {activeIndex === 2 /* Analytics Dashboard - Market Research only */ ? <div className="flex-1 flex flex-col lg:flex-row gap-4" data-id="element-3142">
                        {/* Left: Table */}
                        <div className="flex-[3] bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 flex flex-col" data-id="element-3143">
                          <div className="flex items-start gap-3 mb-5" data-id="element-3144">
                            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3145">
                              <SparklesIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-3146" />
                            </div>
                            <p className="text-sm text-slate-300 leading-relaxed" data-id="element-3147">
                              {dashboardData[2].header}
                            </p>
                          </div>

                          <div className="flex-1 overflow-hidden" data-id="element-3148">
                            <table className="w-full text-left text-xs" data-id="element-3149">
                              <thead data-id="element-3150">
                                <tr className="text-slate-500 border-b border-slate-700/50" data-id="element-3151">
                                  <th className="pb-3 font-medium w-8" data-id="element-3152">#</th>
                                  <th className="pb-3 font-medium" data-id="element-3153">Company</th>
                                  <th className="pb-3 font-medium" data-id="element-3154">Domain</th>
                                  <th className="pb-3 font-medium" data-id="element-3155">Size</th>
                                  <th className="pb-3 font-medium text-right" data-id="element-3156">
                                    Score
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-700/30" data-id="element-3157">
                                {dashboardData[2].table.map((row, i) => <tr key={i} className="text-slate-300" data-id="element-3158">
                                    <td className="py-3 text-slate-500" data-id="element-3159">
                                      {i + 1}
                                    </td>
                                    <td className="py-3 font-medium text-slate-200" data-id="element-3160">
                                      {row.name}
                                    </td>
                                    <td className="py-3 text-slate-400" data-id="element-3161">
                                      {row.domain}
                                    </td>
                                    <td className="py-3 text-slate-400" data-id="element-3162">
                                      {row.size}
                                    </td>
                                    <td className="py-3 text-right" data-id="element-3163">
                                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 font-bold border border-blue-500/20" data-id="element-3164">
                                        {row.score}
                                      </span>
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Right: Charts */}
                        <div className="flex-[2] flex flex-col gap-4" data-id="element-3165">
                          {/* Bar Chart Card */}
                          <div className="flex-1 bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex flex-col" data-id="element-3166">
                            <div className="flex items-center gap-2 mb-4" data-id="element-3167">
                              <BarChart3Icon className="w-4 h-4 text-blue-400" data-id="element-3168" />
                              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-3169">
                                {dashboardData[2].barChartTitle}
                              </h4>
                            </div>
                            <div className="flex-1 flex items-end gap-1.5 pt-2" data-id="element-3170">
                              {dashboardData[2].barChartData.map((val, i) => <div key={i} className="flex-1 flex flex-col justify-end h-full" data-id="element-3171">
                                  <motion.div initial={{
                              height: 0
                            }} animate={{
                              height: `${val / Math.max(...dashboardData[2].barChartData) * 100}%`
                            }} transition={{
                              duration: 0.5,
                              delay: i * 0.05
                            }} className={`w-full rounded-t-sm ${i === 0 ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-3172" />
                                </div>)}
                            </div>
                          </div>

                          {/* Donut Chart Card */}
                          <div className="flex-1 bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex flex-col" data-id="element-3173">
                            <div className="flex items-center gap-2 mb-4" data-id="element-3174">
                              <PieChartIcon className="w-4 h-4 text-blue-400" data-id="element-3175" />
                              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-3176">
                                {dashboardData[2].donutTitle}
                              </h4>
                            </div>
                            <div className="flex items-center gap-5" data-id="element-3177">
                              <div className="relative w-16 h-16 rounded-full shrink-0" style={{
                            background: dashboardData[2].donutGradient
                          }} data-id="element-3178">
                                <div className="absolute inset-3 bg-slate-900 rounded-full" data-id="element-3179" />
                              </div>
                              <div className="flex-1 space-y-2" data-id="element-3180">
                                {dashboardData[2].donutData.map((item, i) => <div key={i} className="flex items-center justify-between text-[10px]" data-id="element-3181">
                                    <div className="flex items-center gap-2" data-id="element-3182">
                                      <div className={`w-2 h-2 rounded-sm ${item.color}`} data-id="element-3183" />
                                      <span className="text-slate-400" data-id="element-3184">
                                        {item.label}
                                      </span>
                                    </div>
                                    <span className="font-medium text-slate-300" data-id="element-3185">
                                      {item.value}
                                    </span>
                                  </div>)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> /* Features Checklist - Lead Gen & Partnerships */ : <div className="grid sm:grid-cols-2 gap-4" data-id="element-3186">
                        {activeCase.features.map((feature, idx) => <motion.div key={idx} initial={{
                      opacity: 0,
                      y: 8
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.3,
                      delay: idx * 0.08
                    }} className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 transition-colors" data-id="element-3187">
                            <CheckCircle2Icon className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-3188" />
                            <span className="text-sm font-medium text-slate-300" data-id="element-3189">
                              {feature}
                            </span>
                          </motion.div>)}
                      </div>}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-6 pt-5 border-t border-slate-800 flex justify-end" data-id="element-3190">
                  <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors" data-id="element-3191">
                    Explore {activeCase.title.toLowerCase()}
                    <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-3192" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}